// We set here the default values of new features
// to safely init our app in case we do smth wrong in firebase

import { isVersionAtLeast, isVersionValid } from './utils/version';

type FeatureFlagValue = {
    web?: string | number | boolean;
    mobile?: string | number | boolean;
};

type FeatureFlags = Record<string, FeatureFlagValue>;

const featureFlags: FeatureFlags = {
    // EG: test_feature: { web: false, mobile: false },
    // Disable v3 streaming sync and use legacy v2+v1 fallback instead
    // When true, forces fallback mode permanently (no v3 attempts)
    disable_v3_sync: { mobile: true },
    disable_v3_push: { mobile: true },
    ai_feature_enabled_v2: { mobile: false },
    // Minimum app version the AI feature is enabled from; blank means no minimum
    ai_feature_from_version: { mobile: '' },
    // JSON array of user emails; blank/empty array means all users have access
    ai_form_to_entry_users: { mobile: '[]' },
};

const getFeatureFlagsInitValuesFor = (platform: 'web' | 'mobile') => {
    const keys = Object.keys(featureFlags);
    const defaultValues = keys.reduce(
        (prev, key) => ({ ...prev, [key]: featureFlags[key][platform] }),
        {},
    );
    return defaultValues;
};

// Raw AI remote config values, kept unparsed so getAiFeatureFlags owns the parsing.
type AiRemoteConfig = {
    isAiFeatureEnabled: boolean;
    aiFeatureFromVersion: string;
    aiFormToEntryUsers: string;
};

/** Default AI remote config, read from the mobile flag defaults so the keys stay next to their definitions. */
function getDefaultAiRemoteConfig(): AiRemoteConfig {
    return {
        isAiFeatureEnabled: Boolean(featureFlags.ai_feature_enabled_v2.mobile),
        aiFeatureFromVersion: String(
            featureFlags.ai_feature_from_version.mobile ?? '',
        ),
        aiFormToEntryUsers: String(
            featureFlags.ai_form_to_entry_users.mobile ?? '',
        ),
    };
}

/** Reads the JSON email allowlist, falling back to empty (= everyone) when blank or malformed. */
function parseAiFormToEntryUsers(raw: unknown): string[] {
    if (!raw) {
        return [];
    }
    try {
        const parsed = JSON.parse(String(raw));
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

type AiFeatureFlagsInput = {
    /** ai_feature_enabled_v2 */
    isAiFeatureEnabled: boolean;

    /** ai_feature_from_version; blank means no minimum version */
    aiFeatureFromVersion: unknown;

    /** Version of the running app, eg Device.getVersion() */
    currentAppVersion: unknown;

    /** ai_form_to_entry_users, raw or already parsed */
    aiFormToEntryUsers: unknown;

    currentUserEmail?: string | null;
};

type AiFeatureFlags = {
    isAiFormToEntryEnabled: boolean;
};

/** Turns the raw AI remote config values into the per-feature booleans the app consumes. */
function getAiFeatureFlags({
    isAiFeatureEnabled,
    aiFeatureFromVersion,
    currentAppVersion,
    aiFormToEntryUsers,
    currentUserEmail,
}: AiFeatureFlagsInput): AiFeatureFlags {
    // No usable minimum version configured means every version qualifies
    const versionAllowed =
        !isVersionValid(aiFeatureFromVersion) ||
        isVersionAtLeast(currentAppVersion, aiFeatureFromVersion);

    if (!isAiFeatureEnabled || !versionAllowed) {
        return { isAiFormToEntryEnabled: false };
    }

    const allowedUsers = Array.isArray(aiFormToEntryUsers)
        ? aiFormToEntryUsers
        : parseAiFormToEntryUsers(aiFormToEntryUsers);

    // Empty allowlist means the feature is enabled for all users
    if (allowedUsers.length === 0) {
        return { isAiFormToEntryEnabled: true };
    }

    return {
        isAiFormToEntryEnabled:
            !!currentUserEmail && allowedUsers.includes(currentUserEmail),
    };
}

export {
    getAiFeatureFlags,
    getDefaultAiRemoteConfig,
    getFeatureFlagsInitValuesFor,
};

export type { AiRemoteConfig };
